package com.patronat.top.features.dashboard.presentation.reviewmoderation

import androidx.lifecycle.viewModelScope
import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.dto.ReviewAiStatus
import com.patronat.top.dto.ReviewFilterRequest
import com.patronat.top.dto.ReviewResponse
import com.patronat.top.features.dashboard.domain.usecase.GetReviewModerationQueueUseCase
import com.patronat.top.features.dashboard.domain.usecase.ModerateReviewUseCase
import com.patronat.top.features.dashboard.domain.usecase.command.GetReviewModerationQueueCommand
import com.patronat.top.features.dashboard.domain.usecase.command.ModerateReviewCommand
import com.patronat.top.features.venues.domain.usecase.GetVenueByIdUseCase
import com.patronat.top.features.venues.domain.usecase.command.GetVenueByIdCommand
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.error_alert_title

class ReviewAiModerationViewModel(
    private val alertController: AlertController,
    private val getReviewModerationQueueUseCase: GetReviewModerationQueueUseCase,
    private val moderateReviewUseCase: ModerateReviewUseCase,
    private val getVenueByIdUseCase: GetVenueByIdUseCase
) : BaseViewModel<ReviewAiModerationState, ReviewAiModerationEvent, ReviewAiModerationEffect>(
    initialState = ReviewAiModerationState()
) {

    private var searchJob: Job? = null

    override fun onEvent(event: ReviewAiModerationEvent) {
        when (event) {
            ReviewAiModerationEvent.Initialize -> loadData(page = 1)
            ReviewAiModerationEvent.OnReload -> loadData(page = currentState.currentPage)
            is ReviewAiModerationEvent.OnSearchChanged -> {
                updateState { it.copy(searchQuery = event.query) }
                searchJob?.cancel()
                searchJob = viewModelScope.launch {
                    delay(500)
                    loadData(page = 1)
                }
            }
            is ReviewAiModerationEvent.OnStatusFilterChanged -> {
                val selected = event.filterIds.takeLast(1)
                    .ifEmpty { listOf(ReviewModerationStatusFilterPreset.NEW.id) }
                updateState { it.copy(selectedStatusFilterIds = selected) }
                loadData(page = 1)
            }
            is ReviewAiModerationEvent.OnPageChanged -> loadData(page = event.page)
            is ReviewAiModerationEvent.OnPageSizeChanged -> {
                updateState { it.copy(pageSize = event.pageSize, currentPage = 1) }
                loadData(page = 1)
            }
            is ReviewAiModerationEvent.OnApproveClicked -> moderateReview(event.reviewId, approved = true)
            is ReviewAiModerationEvent.OnRejectClicked -> moderateReview(event.reviewId, approved = false)
        }
    }

    private fun loadData(page: Int) {
        val preset = currentState.selectedStatusFilterIds.firstOrNull()
            ?.let(ReviewModerationStatusFilterPreset::fromId)
            ?: ReviewModerationStatusFilterPreset.NEW
        val status = preset.backendStatus

        val request = ReviewFilterRequest(
            page = page,
            pageSize = currentState.pageSize,
            status = status,
            query = currentState.searchQuery.ifBlank { null },
            aiStatuses = listOf(ReviewAiStatus.DONE, ReviewAiStatus.ERROR)
        )

        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            request = { getReviewModerationQueueUseCase.execute(GetReviewModerationQueueCommand(request)) },
            onSuccess = { response ->
                updateState {
                    it.copy(
                        reviews = response.items,
                        totalCount = response.totalCount,
                        currentPage = response.page,
                        pageSize = response.pageSize,
                        error = null
                    )
                }
                loadVenueNames(response.items)
            },
            onError = { _, message ->
                updateState { it.copy(error = message, reviews = emptyList(), totalCount = 0) }
                handleError(
                    message?.let { UiText.DynamicString(it) }
                        ?: UiText.DynamicString("Не вдалося завантажити відгуки для модерації")
                )
            }
        )
    }

    private fun loadVenueNames(reviews: List<ReviewResponse>) {
        val missingVenueIds = reviews
            .map { it.venueId }
            .distinct()
            .filterNot { venueId ->
                currentState.venueNamesById.containsKey(venueId) || currentState.failedVenueIds.contains(venueId)
            }

        missingVenueIds.forEach { venueId ->
            launchNetwork(
                request = { getVenueByIdUseCase.execute(GetVenueByIdCommand(venueId)) },
                onSuccess = { venue ->
                    updateState { state ->
                        state.copy(venueNamesById = state.venueNamesById + (venueId to venue.name))
                    }
                },
                onError = { _, _ ->
                    updateState { state -> state.copy(failedVenueIds = state.failedVenueIds + venueId) }
                }
            )
        }
    }

    private fun moderateReview(reviewId: String, approved: Boolean) {
        if (currentState.actionReviewId != null) return

        launchNetwork(
            onLoading = { loading ->
                updateState { it.copy(actionReviewId = if (loading) reviewId else null) }
            },
            request = {
                moderateReviewUseCase.execute(
                    ModerateReviewCommand(
                        reviewId = reviewId,
                        approved = approved
                    )
                )
            },
            onSuccess = {
                alertController.show(
                    title = UiText.DynamicString(if (approved) "Відгук опубліковано" else "Відгук відхилено"),
                    message = UiText.DynamicString("Статус відгуку оновлено."),
                    type = NotificationType.SUCCESS
                )
                loadData(currentState.currentPage)
            },
            onError = { _, message ->
                handleError(
                    message?.let { UiText.DynamicString(it) }
                        ?: UiText.DynamicString("Не вдалося оновити статус відгуку")
                )
            }
        )
    }

    private fun handleError(message: UiText) {
        alertController.show(
            title = UiText.StringResource(DesignRes.string.error_alert_title),
            message = message,
            type = NotificationType.ERROR
        )
    }
}
